import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, InputGroup, Card, Modal, Row, Col, Badge, Spinner } from 'react-bootstrap';
import api from '../../utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faPlus, faEdit, faTrash, faBuilding, 
  faCalendarAlt, faDollarSign, faSync, faHistory
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';
import ErrorBoundary from '../../components/ErrorBoundary';
import EmptyStateMessage from '../../components/EmptyStateMessage';

// Helper functions
const normalizeNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    if (cleaned === '' || cleaned === '.' || cleaned === '-') return 0;
    
    const number = parseFloat(cleaned);
    return isNaN(number) ? 0 : number;
  }
  
  return 0;
};

const formatRupiah = (value) => {
  if (value === undefined || value === null || value === '') {
    return 'Rp 0';
  }
  
  let numValue;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    numValue = cleaned ? parseFloat(cleaned) : 0;
  } else {
    numValue = Number(value);
  }
  
  if (isNaN(numValue)) {
    console.warn('Invalid number format for value:', value);
    return 'Rp 0';
  }
  
  try {
    return `Rp ${numValue.toLocaleString('id-ID')}`;
  } catch (err) {
    console.error('Error formatting number:', err);
    return `Rp ${numValue}`;
  }
};

// Kategori Aset
const assetCategories = [
  'Tanah',
  'Bangunan',
  'Kendaraan',
  'Mesin Produksi',
  'Peralatan Kantor',
  'Elektronik',
  'Furnitur',
  'Software',
  'Lainnya'
];

// Opsi kondisi aset
const conditionOptions = [
  { value: 'baik', label: 'Baik', variant: 'success' },
  { value: 'cukup', label: 'Cukup', variant: 'info' },
  { value: 'buruk', label: 'Buruk', variant: 'warning' },
  { value: 'rusak', label: 'Rusak', variant: 'danger' }
];

const DaftarAset = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    acquisition_date: '',
    acquisition_value: '0',
    current_value: '0',
    condition: 'baik',
    location: '',
    notes: '',
    sku: '', // tambahkan field SKU
    unit: 'unit' // tambahkan default unit
  });
  
  // State untuk modal histori
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState({
    id: null,
    name: ''
  });
  
  // State untuk filter status
  const [statusFilter, setStatusFilter] = useState('all');

  // Tambahkan state untuk kategori dinamis
  const [dynamicCategories, setDynamicCategories] = useState([]);

  // Tambahkan state untuk mode debug
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    fetchAssets();
    // Panggil fetchCategories setelah assets dimuat
  }, []);
  
  // Tambahkan useEffect baru yang bereaksi pada perubahan assets
  useEffect(() => {
    // Hanya panggil fetchCategories jika assets sudah dimuat
    if (assets && assets.length > 0) {
      fetchCategories();
    }
  }, [assets]);

  useEffect(() => {
    if (editAsset) {
      // Format tanggal dengan benar
      let formattedDate = editAsset.acquisition_date;
      
      // Handle case dimana tanggal adalah "-" atau format tidak valid
      if (formattedDate === '-' || !formattedDate) {
        // Default ke tanggal hari ini dalam format YYYY-MM-DD
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      } else if (formattedDate.includes('/')) {
        // Konversi format dd/mm/yyyy ke yyyy-mm-dd
        const parts = formattedDate.split('/');
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
  
      // Gunakan price dari backend jika acquisition_value kosong atau nol
      const acquisitionValue = editAsset.acquisition_value !== undefined && editAsset.acquisition_value > 0 
                                ? editAsset.acquisition_value 
                                : (editAsset.price || 0);
                                
      // Gunakan price dari backend jika current_value kosong atau nol                          
      const currentValue = editAsset.current_value !== undefined && editAsset.current_value > 0
                            ? editAsset.current_value
                            : (editAsset.price || acquisitionValue || 0);
  
      setFormData({
        name: editAsset.name || '',
        category: formatAssetCategory(editAsset.asset_type),
        acquisition_date: formattedDate,
        acquisition_value: String(acquisitionValue),
        current_value: String(currentValue),
        condition: editAsset.condition || 'baik',
        location: editAsset.location || '',
        notes: editAsset.notes || '',
        sku: editAsset.sku || '',
        unit: editAsset.unit || 'unit'
      });
      
      // Log untuk debugging
      console.log("Edit asset data:", {
        original: editAsset,
        formatted: {
          name: editAsset.name || '',
          category: formatAssetCategory(editAsset.asset_type),
          acquisition_date: formattedDate,
          acquisition_value: String(acquisitionValue),
          current_value: String(currentValue),
          condition: editAsset.condition || 'baik',
          location: editAsset.location || '',
          notes: editAsset.notes || '',
          sku: editAsset.sku || '',
          unit: editAsset.unit || 'unit'
        }
      });
    }
  }, [editAsset]);

  // Perbaiki fungsi fetchAssets menggunakan pendekatan dari DaftarPeralatan
  const fetchAssets = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }
      
      // Timestamp untuk mencegah caching
      const timestamp = new Date().getTime();
      const url = `https://rumahakrilik.id/api/inventory/?category=asset&nocache=${timestamp}`;
      
      // Tambahkan header yang lebih kuat untuk mencegah caching
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'X-Requested-With': 'XMLHttpRequest' // Penting untuk bypass cache
      };
      
      console.log("Fetching asset data...");
      const response = await axios.get(url, { headers });
      
      console.log('Raw API response:', response);
      
      let assetList = [];
      
      try {
        if (Array.isArray(response.data)) {
          assetList = response.data;
          console.log("Data dalam format array:", assetList.length);
        } else if (response.data.results && Array.isArray(response.data.results)) {
          assetList = response.data.results;
          console.log("Data dalam format paginated:", assetList.length);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          assetList = response.data.data;
          console.log("Data dalam format data wrapper:", assetList.length);
        } else {
          console.error("Unexpected data format:", response.data);
          throw new Error("Format data tidak valid");
        }
        
        // Tangani kasus dimana assetList mungkin null atau bukan array
        if (!assetList || !Array.isArray(assetList)) {
          console.error("Asset list is not an array:", assetList);
          assetList = []; // Fallback ke array kosong
        }
        
        // Filter untuk hanya mengambil aset
        assetList = assetList.filter(item => 
          item && (
            item.category === 'asset' || 
            item.asset_type || 
            item.type === 'asset'
          )
        );
        
        // Perbaiki fungsi standardizedAssets untuk menangkap field price dari backend
        const standardizedAssets = assetList.map(asset => {
          try {
            // Log data mentah untuk debugging
            console.log("Processing raw asset:", asset);
            
            // Simpan semua data original dari backend
            return {
              ...asset,
              id: asset.id || 0,
              name: asset.name || 'Tidak ada nama',
              asset_type: formatAssetCategory(asset.asset_type || asset.subcategory),
              category: 'asset',
              acquisition_date: asset.acquisition_date || '-',
              // Gunakan price jika acquisition_value tidak ada
              acquisition_value: normalizeNumber(asset.acquisition_value) || normalizeNumber(asset.price),
              // Gunakan price jika current_value tidak ada
              current_value: normalizeNumber(asset.current_value) || normalizeNumber(asset.price),
              condition: asset.condition || 'baik',
              location: asset.location || '-',
              notes: asset.notes || '-',
              current_stock: asset.current_stock || 1,
              minimum_stock: asset.minimum_stock || 0,
              unit: asset.unit || 'unit',
              sku: asset.sku || `ASSET-${asset.id || Date.now()}`
            };
          } catch (itemError) {
            console.error("Error processing asset:", itemError, asset);
            // Return minimal valid asset object jika terjadi error
            return {
              id: asset.id || 0,
              name: asset.name || "Error: Data tidak valid",
              asset_type: "Lainnya",
              category: "asset",
              condition: "baik",
              acquisition_value: normalizeNumber(asset.price) || 0,
              current_value: normalizeNumber(asset.price) || 0
            };
          }
        });
        
        // Sortir berdasarkan kondisi
        const sortedAssets = standardizedAssets.sort((a, b) => {
          const conditionOrder = {
            'rusak': 0,
            'buruk': 1,
            'cukup': 2,
            'baik': 3
          };
          
          const condA = conditionOrder[a.condition] !== undefined ? conditionOrder[a.condition] : 3;
          const condB = conditionOrder[b.condition] !== undefined ? conditionOrder[b.condition] : 3;
          
          return condA - condB;
        });
        
        setAssets(sortedAssets);
        
        // Backup cache lokal
        try {
          localStorage.setItem('rumahAkrilik_assetBackup', JSON.stringify(sortedAssets));
          localStorage.setItem('rumahAkrilik_assetBackupDate', new Date().toISOString());
          console.log("Auto backup to localStorage successful");
        } catch (backupError) {
          console.error("Error backing up to localStorage:", backupError);
        }
      } catch (processingError) {
        console.error("Error processing asset data:", processingError);
        attemptRecoveryFromLocalStorage();
        throw processingError;
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      
      // Coba dengan direct call ke API tanpa parameter
      try {
        console.log('Trying direct API call...');
        const token = localStorage.getItem('jwtToken');
        
        const fallbackResponse = await axios.get('https://rumahakrilik.id/api/inventory/', {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (fallbackResponse.status === 200) {
          try {
            console.log("Fallback API response:", fallbackResponse.data);
            let assetList = [];
            
            if (Array.isArray(fallbackResponse.data)) {
              assetList = fallbackResponse.data;
            } else if (fallbackResponse.data.results && Array.isArray(fallbackResponse.data.results)) {
              assetList = fallbackResponse.data.results;
            } else if (fallbackResponse.data.data && Array.isArray(fallbackResponse.data.data)) {
              assetList = fallbackResponse.data.data;
            }
            
            // Filter untuk asset saja
            assetList = assetList.filter(item => 
              item && (
                item.category === 'asset' || 
                item.asset_type || 
                item.type === 'asset'
              )
            );
            
            if (Array.isArray(assetList) && assetList.length > 0) {
              const standardizedAssets = assetList.map(asset => ({
                id: asset.id || 0,
                name: asset.name || 'Tidak ada nama',
                asset_type: asset.asset_type || asset.subcategory || 'Lainnya',
                category: 'asset',
                acquisition_date: asset.acquisition_date || '-',
                acquisition_value: normalizeNumber(asset.acquisition_value || asset.price),
                current_value: normalizeNumber(asset.current_value || asset.price || asset.acquisition_value),
                condition: asset.condition || 'baik',
                location: asset.location || '-',
                notes: asset.notes || '-',
                current_stock: asset.current_stock || 1,
                minimum_stock: asset.minimum_stock || 0,
                unit: asset.unit || 'unit',
                sku: asset.sku || `ASSET-${asset.id || Date.now()}`
              }));
              
              console.log("Recovered using fallback method:", standardizedAssets.length, "items");
              setAssets(standardizedAssets);
              
              // Update cache lokal dengan data terbaru
              localStorage.setItem('rumahAkrilik_assetBackup', JSON.stringify(standardizedAssets));
              localStorage.setItem('rumahAkrilik_assetBackupDate', new Date().toISOString());
              
              toast.info('Data diambil dengan metode alternatif');
            } else {
              console.warn("Fallback returned no asset items");
              attemptRecoveryFromLocalStorage();
            }
          } catch (processingError) {
            console.error("Error processing fallback data:", processingError);
            attemptRecoveryFromLocalStorage();
          }
        } else {
          attemptRecoveryFromLocalStorage();
        }
      } catch (fallbackError) {
        console.error('Fallback method failed:', fallbackError);
        attemptRecoveryFromLocalStorage();
      }
    } finally {
      setLoading(false);
    }
  };

// Tambahkan fungsi attemptRecoveryFromLocalStorage seperti di DaftarPeralatan
const attemptRecoveryFromLocalStorage = () => {
  try {
    const backupData = localStorage.getItem('rumahAkrilik_assetBackup');
    if (backupData) {
      const parsedBackup = JSON.parse(backupData);
      if (Array.isArray(parsedBackup) && parsedBackup.length > 0) {
        setAssets(parsedBackup);
        toast.warning("Gagal memuat data dari server. Menggunakan data cache lokal.");
        return;
      }
    }
    setAssets([]);
    toast.error('Gagal memuat data aset dan tidak ada cache lokal');
  } catch (localErr) {
    console.error("Error accessing local backup:", localErr);
    setAssets([]);
    toast.error('Gagal memuat data aset');
  }
};

// Perbaikan fungsi handleCreate menggunakan pendekatan dari DaftarPeralatan
const handleCreate = async () => {
  try {
    if (!validateForm()) return;
    
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      toast.error('Sesi login telah berakhir');
      return;
    }
    
    // Nilai aset dikonversi ke string untuk memastikan kompatibilitas API
    const acquisitionValue = String(normalizeNumber(formData.acquisition_value));
    const currentValue = String(normalizeNumber(formData.current_value));
    
    // Format tanggal dengan benar
    const formattedDate = formatDateForAPI(formData.acquisition_date);
    
    // Buat SKU unik menggunakan timestamp dan nama aset
    // Bersihkan nama aset dari karakter khusus dan spasi untuk SKU
    const cleanName = formData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10).toUpperCase();
    const uniqueSku = formData.sku || `AST-${cleanName}-${Date.now().toString().slice(-6)}`;

    // Format data aset untuk API dengan struktur yang lebih lengkap
    const assetData = {
      name: formData.name.trim(),
      category: 'asset',
      asset_type: formData.category,
      subcategory: formData.category,
      acquisition_date: formattedDate,
      acquisition_value: acquisitionValue,
      current_value: currentValue,
      condition: formData.condition,
      location: formData.location || '',
      notes: formData.notes || '',
      unit: formData.unit || 'unit',
      minimum_stock: '1',
      current_stock: '1',
      sku: uniqueSku
    };
    
    // Log data yang akan dikirim
    console.log("Data yang akan dikirim:", assetData);
    
    // Temporary item untuk optimistic UI update
    const tempItem = {
      id: `temp-${Date.now()}`,
      name: formData.name.trim(),
      asset_type: formData.category,
      category: 'asset',
      acquisition_date: formattedDate,
      acquisition_value: normalizeNumber(formData.acquisition_value),
      current_value: normalizeNumber(formData.current_value),
      condition: formData.condition,
      location: formData.location || '',
      notes: formData.notes || '',
      unit: formData.unit || 'unit',
      current_stock: 1,
      minimum_stock: 1,
      sku: uniqueSku
    };
    
    try {
      setLoading(true);
      
      // TAMBAHAN: Optimistic UI Update (sebelum request ke server)
      setAssets(prevAssets => [...prevAssets, tempItem]);
      
      const response = await axios.post(
        'https://rumahakrilik.id/api/inventory/',
        assetData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );
      
      console.log("Response dari server:", response);
      
      if (response.status === 201 || response.status === 200) {
        toast.success('Aset berhasil ditambahkan');
        
        // Perbarui state dengan data dari server
        const serverItem = response.data;
        
        // Update item ID dengan ID dari server
        setAssets(prevAssets => {
          return prevAssets.map(item => {
            if (item.id === tempItem.id) {
              return {
                ...item,
                id: serverItem.id,
                sku: serverItem.sku || item.sku
              };
            }
            return item;
          });
        });
        
        // Reset form & close modal
        setShowModal(false);
        resetForm();
        
        // Update cache dengan data terbaru
        setTimeout(() => {
          try {
            const currentAssets = [...assets];
            const itemIndex = currentAssets.findIndex(item => item.id === tempItem.id);
            
            if (itemIndex >= 0) {
              currentAssets[itemIndex] = {
                ...currentAssets[itemIndex],
                id: serverItem.id,
                sku: serverItem.sku || currentAssets[itemIndex].sku
              };
              
              localStorage.setItem('rumahAkrilik_assetBackup', JSON.stringify(currentAssets));
              localStorage.setItem('rumahAkrilik_assetBackupDate', new Date().toISOString());
            }
          } catch (error) {
            console.error("Error updating local cache:", error);
          }
        }, 0);
      }
    } catch (requestError) {
      console.error("Request error:", requestError);
      
      // Hapus item temporary dari UI jika terjadi error
      setAssets(prevAssets => 
        prevAssets.filter(item => item.id !== tempItem.id)
      );
      
      if (requestError.response && requestError.response.status === 400) {
        console.log("Error 400 detail:", requestError.response.data);
        
        // Tangani error dengan format yang lebih informatif
        let errorMessage = 'Gagal menambahkan aset:';
        
        if (typeof requestError.response.data === 'object') {
          Object.entries(requestError.response.data).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              errorMessage += ` ${key}: ${value.join(', ')}`;
            } else {
              errorMessage += ` ${key}: ${value}`;
            }
          });
        } else {
          errorMessage += ` ${requestError.response.data}`;
        }
        
        toast.error(errorMessage);
      } else {
        toast.error(`Gagal menambahkan aset: ${requestError.message}`);
      }
    } finally {
      setLoading(false);
    }
  } catch (error) {
    console.error('Error in handleCreate:', error);
    toast.error(`Terjadi kesalahan: ${error.message}`);
    setLoading(false);
  }
};

// Perbaiki handleUpdate untuk konsistensi dengan pendekatan DaftarPeralatan
const handleUpdate = async () => {
  try {
    if (!validateForm()) return;
    
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      toast.error('Sesi login telah berakhir');
      return;
    }
    
    setLoading(true);
    
    // Pastikan semua nilai numerik dikirim sebagai string untuk API
    const updatePayload = {
      name: formData.name.trim(),
      category: 'asset',
      asset_type: formData.category,
      subcategory: formData.category,
      acquisition_date: formatDateForAPI(formData.acquisition_date),
      acquisition_value: String(normalizeNumber(formData.acquisition_value)),
      current_value: String(normalizeNumber(formData.current_value)),
      condition: formData.condition,
      location: formData.location || '',
      notes: formData.notes || '',
      unit: formData.unit || 'unit',
      minimum_stock: String(editAsset.minimum_stock || 1),
      current_stock: String(editAsset.current_stock || 1),
      sku: formData.sku || editAsset.sku || ''
    };
    
    // Perbarui state secara lokal terlebih dahulu (optimistic update)
    const optimisticUpdatedItem = {
      id: editAsset.id,
      ...updatePayload,
      acquisition_value: normalizeNumber(formData.acquisition_value),
      current_value: normalizeNumber(formData.current_value),
      current_stock: editAsset.current_stock || 1,
      minimum_stock: editAsset.minimum_stock || 1
    };
    
    // Update lokal state terlebih dahulu
    setAssets(prevAssets => {
      return prevAssets.map(item => 
        item.id === editAsset.id ? optimisticUpdatedItem : item
      );
    });
    
    console.log("Sending update to API:", updatePayload);
    console.log("Original item ID:", editAsset.id);
    
    try {
      // Gunakan fetch API yang lebih reliable untuk update
      const response = await fetch(`https://rumahakrilik.id/api/inventory/${editAsset.id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(updatePayload)
      });
      
      // Periksa status response
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error response:", errorText);
        throw new Error(`Error update: ${response.status} ${response.statusText}`);
      }
      
      // Coba parse response
      let responseData;
      try {
        responseData = await response.json();
        console.log("Update response:", responseData);
      } catch (parseError) {
        console.warn("Could not parse response as JSON:", parseError);
        // Tetap lanjutkan karena update mungkin berhasil meskipun format respons tidak valid
      }
      
      toast.success('Aset berhasil diperbarui');
      setShowModal(false);
      resetForm();
      
      // Update cache lokal
      try {
        const currentAssets = [...assets];
        const itemIndex = currentAssets.findIndex(item => item.id === editAsset.id);
        
        if (itemIndex >= 0) {
          const updatedAsset = responseData || optimisticUpdatedItem;
          currentAssets[itemIndex] = {
            ...currentAssets[itemIndex],
            ...updatedAsset
          };
          
          localStorage.setItem('rumahAkrilik_assetBackup', JSON.stringify(currentAssets));
          localStorage.setItem('rumahAkrilik_assetBackupDate', new Date().toISOString());
        }
      } catch (cacheError) {
        console.error("Error updating local cache:", cacheError);
      }
      
      // Sinkronkan data dengan server setelah delay kecil
      setTimeout(() => {
        // Gunakan direct API call untuk item yang diubah
        const refreshUrl = `https://rumahakrilik.id/api/inventory/${editAsset.id}/?nocache=${Date.now()}`;
        fetch(refreshUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }).then(response => response.json())
          .then(itemData => {
            if (itemData && itemData.id) {
              // Update item spesifik yang diubah
              setAssets(prevAssets => {
                const updatedAssets = prevAssets.map(item => {
                  if (item.id === editAsset.id) {
                    return {
                      ...item,
                      name: itemData.name || item.name,
                      asset_type: itemData.asset_type || itemData.subcategory || item.asset_type,
                      acquisition_date: itemData.acquisition_date || item.acquisition_date,
                      acquisition_value: normalizeNumber(itemData.acquisition_value) || item.acquisition_value,
                      current_value: normalizeNumber(itemData.current_value) || item.current_value,
                      condition: itemData.condition || item.condition,
                      location: itemData.location || item.location,
                      notes: itemData.notes || item.notes
                    };
                  }
                  return item;
                });
                return updatedAssets;
              });
              console.log("Item diperbarui dari server:", itemData);
            }
          })
          .catch(err => {
            console.warn("Gagal memuat item spesifik, menggunakan data lokal:", err);
          });
      }, 1000);
    } catch (updateError) {
      console.error("Update error:", updateError);
      toast.error(`Gagal update: ${updateError.message}`);
      
      // Pastikan UI tetap konsisten dengan fetch data terbaru dari server
      fetchAssets(true).catch(err => {
        console.error("Error fetching data after update failure:", err);
      });
    } finally {
      setLoading(false);
    }
  } catch (error) {
    console.error("Error in update process:", error);
    toast.error('Terjadi kesalahan saat memproses update');
    setLoading(false);
  }
};

// Pastikan hanya ada satu definisi refreshData yang berfungsi (fungsi di baris 747)
const refreshData = async () => {
  try {
    setLoading(true);
    toast.info('Menyinkronkan data...');
    
    // Hapus cache local storage dahulu untuk memastikan data baru diambil
    localStorage.removeItem('rumahAkrilik_assetBackupDate');
    
    const timestamp = new Date().getTime();
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      toast.error('Sesi login telah berakhir');
      return;
    }
    
    // Array untuk menampung semua data dari semua halaman
    let allAssets = [];
    let currentPage = 1;
    let hasMorePages = true;
    
    // Fungsi untuk mengambil satu halaman data
    const fetchPage = async (page) => {
      const url = `https://rumahakrilik.id/api/inventory/?category=asset&page=${page}&t=${timestamp}`;
      
      console.log(`Fetching page ${page}...`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error status: ${response.status}`);
      }
      
      const responseText = await response.text();
      console.log(`Raw response page ${page}:`, responseText);
      
      // Coba parse JSON
      if (!responseText || responseText.trim() === '') {
        console.warn(`Empty response for page ${page}`);
        return { results: [], next: null };
      }
      
      try {
        const data = JSON.parse(responseText);
        return data;
      } catch (parseError) {
        console.error(`JSON parse error on page ${page}:`, parseError);
        throw new Error(`Tidak dapat memproses data dari server: ${parseError.message}`);
      }
    };
    
    // Loop untuk mengambil semua halaman data
    while (hasMorePages) {
      try {
        const pageData = await fetchPage(currentPage);
        
        // Periksa apakah data dalam format yang diharapkan
        if (Array.isArray(pageData)) {
          // Format array langsung
          allAssets = [...allAssets, ...pageData];
          hasMorePages = false; // Tidak ada data paginasi, jadi tidak ada halaman berikutnya
        } else if (pageData && Array.isArray(pageData.results)) {
          // Format paginasi
          allAssets = [...allAssets, ...pageData.results];
          hasMorePages = !!pageData.next;
        } else if (pageData && pageData.data && Array.isArray(pageData.data)) {
          // Format data wrapper
          allAssets = [...allAssets, ...pageData.data];
          hasMorePages = !!pageData.next_page;
        } else {
          console.warn("Unexpected data format on page", currentPage, pageData);
          hasMorePages = false;
        }
        
        currentPage++;
        
        // Safety limit untuk mencegah loop tak terbatas
        if (currentPage > 10) {
          console.warn("Reached maximum page limit (10), stopping pagination fetch");
          hasMorePages = false;
        }
      } catch (pageError) {
        console.error(`Error fetching page ${currentPage}:`, pageError);
        // Hentikan pagination jika terjadi error
        hasMorePages = false;
      }
    }
    
    console.log(`Total items fetched from all pages: ${allAssets.length}`);
    
    // Filter hanya data asset
    const assetData = allAssets.filter(item => 
      item && (item.category === 'asset' || item.asset_type)
    );
    
    // Proses dan normalisasi data
    if (assetData.length > 0) {
      const processedData = assetData.map(asset => ({
        id: asset.id,
        name: asset.name || 'Tidak ada nama',
        asset_type: formatAssetCategory(asset.asset_type || asset.subcategory),
        category: 'asset',
        acquisition_date: asset.acquisition_date || '-',
        acquisition_value: normalizeNumber(asset.acquisition_value || asset.price),
        current_value: normalizeNumber(asset.current_value || asset.price || asset.acquisition_value),
        condition: asset.condition || 'baik',
        location: asset.location || '-',
        notes: asset.notes || '-',
        sku: asset.sku || `ASSET-${asset.id}`,
        current_stock: asset.current_stock || 1,
        minimum_stock: asset.minimum_stock || 0,
        unit: asset.unit || 'unit'
      }));
      
      // Update state
      setAssets(processedData);
      
      // Update cache lokal
      localStorage.setItem('rumahAkrilik_assetBackup', JSON.stringify(processedData));
      localStorage.setItem('rumahAkrilik_assetBackupDate', new Date().toISOString());
      
      toast.success('Data aset berhasil disinkronkan');
      console.log(`Loaded ${processedData.length} assets successfully`);
    } else {
      console.warn("No asset data found");
      if (assets.length === 0) {
        toast.warning('Tidak ada data aset yang ditemukan');
      } else {
        // Jika sudah ada data sebelumnya, tetap gunakan data tersebut
        toast.info('Tidak ada data baru, menggunakan data yang ada');
      }
    }
  } catch (error) {
    console.error("Error refreshing data:", error);
    toast.error('Gagal menyegarkan data: ' + error.message);
    
    // Coba gunakan data lokal jika refresh gagal
    attemptRecoveryFromLocalStorage();
  } finally {
    setLoading(false);
  }
};
  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus aset ini?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('jwtToken');
        
        if (!token) {
          toast.error('Sesi login telah berakhir');
          return;
        }
        
        const response = await axios.delete(`https://rumahakrilik.id/api/inventory/${id}/`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        if (response.status === 204 || response.status === 200) {
          toast.success('Aset berhasil dihapus');
          fetchAssets(true);
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      } catch (error) {
        console.error('Error deleting asset:', error);
        toast.error('Gagal menghapus aset');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (asset) => {
    setEditAsset(asset);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditAsset(null);
    setFormData({
      name: '',
      category: '',
      acquisition_date: '',
      acquisition_value: '0',
      current_value: '0',
      condition: 'baik',
      location: '',
      notes: '',
      sku: '', // reset field SKU
      unit: 'unit' // reset dengan default unit
    });
  };

  // Fungsi untuk menampilkan modal histori
  const handleShowHistory = (item) => {
    setHistoryItem({
      id: item.id,
      name: item.name
    });
    setShowHistoryModal(true);
  };

  // Tambahkan definisi forceSync yang belum ada
  const forceSync = async () => {
    try {
      // Hapus cache lokal terlebih dahulu
      localStorage.removeItem('rumahAkrilik_assetBackup');
      localStorage.removeItem('rumahAkrilik_assetBackupDate');
      
      // Jalankan refreshData yang sudah terbukti berfungsi baik
      toast.info('Memulai sinkronisasi dengan backend...');
      await refreshData();
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(`Gagal melakukan sinkronisasi: ${error.message}`);
    }
  };

  // Fungsi filter aset berdasarkan kondisi
  const getFilteredAssets = () => {
    if (!assets || assets.length === 0) return [];
    
    // Filter berdasarkan kata kunci pencarian
    let filteredBySearch = assets;
    if (searchTerm) {
      filteredBySearch = assets.filter(asset => 
        asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.asset_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter berdasarkan kondisi
    let result = filteredBySearch;
    switch(statusFilter) {
      case 'baik':
        result = filteredBySearch.filter(item => item.condition === 'baik');
        break;
      case 'cukup':
        result = filteredBySearch.filter(item => item.condition === 'cukup');
        break;
      case 'buruk':
        result = filteredBySearch.filter(item => item.condition === 'buruk');
        break;
      case 'rusak':
        result = filteredBySearch.filter(item => item.condition === 'rusak');
        break;
      default:
        // 'all' - tidak perlu filter tambahan
        break;
    }
    
    return result;
  };

  // Helper untuk memastikan format kategori yang konsisten
  const formatAssetCategory = (category) => {
    try {
      // Pastikan kategori tidak kosong
      if (!category) return 'Lainnya';
      
      // Jika kategori sudah ada dalam daftar yang valid, gunakan apa adanya
      if (assetCategories.includes(category)) return category;
      
      // Cek jika kategori adalah string dengan case yang berbeda
      const matchedCategory = assetCategories.find(
        validCategory => validCategory.toLowerCase() === String(category).toLowerCase()
      );
      if (matchedCategory) return matchedCategory;
      
      // Jika kategori adalah string lain, kembalikan sebagai string original
      // Ini membantu jika backend mengirim kategori yang belum ada di frontend
      return String(category) || 'Lainnya';
    } catch (error) {
      console.error("Error formatting category:", error);
      return 'Lainnya'; // Default fallback
    }
  };

  const getConditionBadge = (condition) => {
    const option = conditionOptions.find(opt => opt.value === condition) || conditionOptions[0];
    return <Badge bg={option.variant}>{option.label}</Badge>;
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    
    if (editAsset) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  // Perbaiki fungsi fetchCategories untuk mengatasi error 404
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return;
      
      // Gunakan pendekatan alternatif daripada mengandalkan endpoint khusus categories
      // yang mungkin tidak ada di API
      console.log("Fetching categories from inventory data...");
      
      try {
        // Coba ambil dari semua item inventory yang sudah ada
        if (assets && assets.length > 0) {
          // Ekstrak kategori unik dari data aset yang sudah dimuat
          const uniqueCategories = [...new Set(
            assets
              .filter(asset => asset && asset.asset_type)
              .map(asset => asset.asset_type)
          )];
          
          // Tambahkan kategori default jika belum ada
          assetCategories.forEach(defaultCat => {
            if (!uniqueCategories.includes(defaultCat)) {
              uniqueCategories.push(defaultCat);
            }
          });
          
          console.log("Extracted categories from existing assets:", uniqueCategories);
          setDynamicCategories(uniqueCategories);
          return;
        }
        
        // Jika assets masih kosong, ambil data dari API inventory
        console.log("No existing assets, fetching from API...");
        const timestamp = new Date().getTime();
        const response = await axios.get(`https://rumahakrilik.id/api/inventory/?category=asset&t=${timestamp}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        // Proses data untuk ekstrak kategori unik
        let assetList = [];
        if (Array.isArray(response.data)) {
          assetList = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          assetList = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          assetList = response.data.data;
        }
        
        if (assetList.length > 0) {
          // Ekstrak kategori unik
          const apiCategories = [...new Set(
            assetList
              .filter(item => item && (item.asset_type || item.subcategory))
              .map(item => item.asset_type || item.subcategory)
              .filter(cat => cat && cat.trim() !== '')
          )];
          
          // Tambahkan kategori default
          const combinedCategories = [...new Set([
            ...apiCategories,
            ...assetCategories
          ])];
          
          console.log("Categories from API:", combinedCategories);
          setDynamicCategories(combinedCategories);
        } else {
          // Jika tidak ada data dari API, gunakan kategori default
          console.log("Using default asset categories");
          setDynamicCategories(assetCategories);
        }
      } catch (apiError) {
        console.error("Error fetching from inventory API:", apiError);
        // Fallback ke kategori default
        setDynamicCategories(assetCategories);
      }
    } catch (error) {
      console.error("Error in fetchCategories:", error);
      // Fallback ke kategori default jika gagal
      setDynamicCategories(assetCategories);
    }
  };

// Tambahkan definisi fungsi validateForm
const validateForm = () => {
  // Validasi nama
  if (!formData.name || formData.name.trim() === '') {
    toast.error('Nama aset tidak boleh kosong');
    return false;
  }
  
  // Validasi kategori
  if (!formData.category || formData.category.trim() === '') {
    toast.error('Kategori aset harus dipilih');
    return false;
  }
  
  // Validasi nilai aset
  if (normalizeNumber(formData.acquisition_value) <= 0) {
    toast.error('Nilai perolehan aset harus lebih dari 0');
    return false;
  }
  
  // Jika tanggal perolehan kosong, tambahkan warning tapi tetap boleh submit
  if (!formData.acquisition_date || formData.acquisition_date.trim() === '') {
    toast.warning('Tanggal perolehan kosong. Akan menggunakan tanggal hari ini.');
  }
  
  return true;
};

// Tambahkan definisi fungsi formatDateForAPI
const formatDateForAPI = (dateString) => {
  if (!dateString) {
    // Jika tidak ada tanggal, gunakan tanggal hari ini
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  }
  
  try {
    // Jika format sudah YYYY-MM-DD, gunakan langsung
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Jika format DD/MM/YYYY, konversi ke YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const parts = dateString.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    // Coba parse dan format menggunakan Date object
    const dateObj = new Date(dateString);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    }
    
    // Jika tidak bisa diformat, gunakan tanggal hari ini
    const today = new Date();
    return today.toISOString().split('T')[0];
  } catch (error) {
    console.error("Error formatting date:", error);
    // Fallback ke tanggal hari ini
    const today = new Date();
    return today.toISOString().split('T')[0];
  }
};
  const handleRefreshClick = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      toast.info('Memuat ulang data...');
      
      // Simpan state aset saat ini sebagai cadangan
      const currentAssets = [...assets];
      
      try {
        // Gunakan refreshData untuk konsistensi dengan tombol Sinkronisasi
        await refreshData();
        toast.success('Data berhasil dimuat ulang');
      } catch (error) {
        console.error('Error refreshing asset data:', error);
        
        // Kembalikan state ke data sebelumnya jika gagal dan tidak ada recovery
        if (assets.length === 0 && currentAssets.length > 0) {
          setAssets(currentAssets);
          toast.info('Menggunakan data sebelumnya');
        } else if (assets.length === 0) {
          toast.error('Gagal memuat data aset');
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <ErrorBoundary>
      <MainLayout>
        <Container fluid className="py-4">
          <h1 className="mb-4">Daftar Aset Perusahaan</h1>
          
          <div className="d-flex justify-content-between mb-3">
            <InputGroup style={{ maxWidth: '400px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Cari berdasarkan nama, kategori, lokasi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <div>
              <Button 
                variant="warning" 
                className="me-2" 
                onClick={forceSync}
                disabled={loading}
              >
                {loading ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Sinkronisasi...</>
                ) : (
                  <><FontAwesomeIcon icon={faSync} className="me-1" /> Sinkronisasi</>
                )}
              </Button>
              <Button 
                variant="outline-secondary" 
                className="me-2" 
                onClick={handleRefreshClick}
                disabled={loading}
              >
                {loading ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Memuat...</>
                ) : (
                  <><FontAwesomeIcon icon={faSync} className="me-1" /> Refresh Data</>
                )}
              </Button>
              <Button 
                variant="primary" 
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" /> Tambah Aset
              </Button>
            </div>
          </div>

          {/* Tambahkan UI komponen debug di bawah tombol Refresh Data */}
          <div className="mb-3">
            <Button 
              variant="light"
              size="sm" 
              onClick={() => setDebugMode(!debugMode)}
              className="me-2"
            >
              {debugMode ? 'Sembunyikan Debug' : 'Tampilkan Debug'}
            </Button>
            
            {debugMode && (
              <div className="p-2 bg-light border rounded mt-1">
                <p><small>Jumlah Data Aset: {assets?.length || 0}</small></p>
                <p><small>Kategori Tersedia: {dynamicCategories.length}</small></p>
                <pre style={{fontSize: '0.7rem', maxHeight: '100px', overflow: 'auto'}}>
                  {JSON.stringify(assets?.slice(0, 2), null, 2)}
                </pre>
              </div>
            )}
          </div>

          <Card>
            <Card.Body>
              {/* Filter kondisi seperti di DaftarStockBahan.jsx */}
              <div className="mb-3">
                <button 
                  className={`btn btn-sm mx-1 ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setStatusFilter('all')}
                >
                  Semua
                </button>
                <button 
                  className={`btn btn-sm mx-1 ${statusFilter === 'baik' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setStatusFilter('baik')}
                >
                  Baik
                </button>
                <button 
                  className={`btn btn-sm mx-1 ${statusFilter === 'cukup' ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => setStatusFilter('cukup')}
                >
                  Cukup
                </button>
                <button 
                  className={`btn btn-sm mx-1 ${statusFilter === 'buruk' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setStatusFilter('buruk')}
                >
                  Buruk
                </button>
                <button 
                  className={`btn btn-sm mx-1 ${statusFilter === 'rusak' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setStatusFilter('rusak')}
                >
                  Rusak
                </button>
              </div>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Memuat data aset...</p>
                </div>
              ) : (
                <>
                  {getFilteredAssets().length === 0 ? (
                    <EmptyStateMessage 
                      message="Tidak ada data aset yang tersedia" 
                      icon={faBuilding}
                      onRefresh={refreshData}
                      actionText="Tambah Aset Baru"
                      onAction={() => {
                        resetForm();
                        setShowModal(true);
                      }}
                    />
                  ) : (
                    <Table responsive striped bordered hover>
                      <thead>
                        <tr>
                          <th>Nama Aset</th>
                          <th>Kategori</th>
                          <th>Tanggal Perolehan</th>
                          <th>Nilai Perolehan</th>
                          <th>Nilai Sekarang</th>
                          <th>Kondisi</th>
                          <th>Lokasi</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredAssets().map((asset) => (
                          <tr key={asset.id}>
                            <td>{asset.name}</td>
                            <td>{asset.asset_type}</td>
                            <td>{asset.acquisition_date}</td>
                            <td>{formatRupiah(asset.acquisition_value)}</td>
                            <td>{formatRupiah(asset.current_value)}</td>
                            <td>{getConditionBadge(asset.condition)}</td>
                            <td>{asset.location}</td>
                            <td>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-1"
                                onClick={() => handleEdit(asset)}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                className="me-1"
                                onClick={() => handleDelete(asset.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                onClick={() => handleShowHistory(asset)}
                              >
                                <FontAwesomeIcon icon={faHistory} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {/* Modal Form */}
          <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                {editAsset ? `Edit Aset: ${editAsset.name}` : 'Tambah Aset Baru'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmitForm}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nama Aset</Form.Label>
                      <Form.Control 
                        type="text" 
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kategori</Form.Label>
                      <Form.Select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        required
                      >
                        <option value="">-- Pilih Kategori Aset --</option>
                        {(dynamicCategories.length > 0 ? dynamicCategories : assetCategories).map((category, index) => (
                          <option key={index} value={category}>{category}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        Tanggal Perolehan
                      </Form.Label>
                      <Form.Control 
                        type="date"
                        required
                        value={formData.acquisition_date}
                        onChange={(e) => setFormData({...formData, acquisition_date: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Lokasi</Form.Label>
                      <Form.Control 
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                        Nilai Perolehan (Rp)
                      </Form.Label>
                      <Form.Control 
                        type="text"
                        required
                        value={formData.acquisition_value}
                        onChange={(e) => {
                          const value = e.target.value.replace(/^0+|[^\d]/g, '');
                          setFormData({...formData, acquisition_value: value});
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                        Nilai Sekarang (Rp)
                      </Form.Label>
                      <Form.Control 
                        type="text"
                        required
                        value={formData.current_value}
                        onChange={(e) => {
                          const value = e.target.value.replace(/^0+|[^\d]/g, '');
                          setFormData({...formData, current_value: value});
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kondisi</Form.Label>
                      <Form.Select
                        value={formData.condition}
                        onChange={(e) => setFormData({...formData, condition: e.target.value})}
                        required
                      >
                        {conditionOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SKU/Kode Aset</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="Kode akan dibuat otomatis jika kosong"
                        value={formData.sku || ''}
                        onChange={(e) => setFormData({...formData, sku: e.target.value})}
                      />
                      <Form.Text className="text-muted">
                        Kode unik untuk aset ini. Akan dibuat otomatis jika tidak diisi.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="unit, buah, lembar, dll"
                        value={formData.unit || 'unit'}
                        onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Detail / Catatan</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
                
                <div className="d-flex justify-content-end">
                  <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                    Batal
                  </Button>
                  <Button variant="primary" type="submit">
                    {editAsset ? 'Perbarui' : 'Simpan'}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          {/* Modal Histori */}
          <HistoryModal 
            show={showHistoryModal} 
            onHide={() => setShowHistoryModal(false)}
            itemId={historyItem.id}
            itemName={historyItem.name}
          />
        </Container>
      </MainLayout>
    </ErrorBoundary>
  );
};

// Komponen Modal Histori dari DaftarStockBahan.jsx
const HistoryModal = ({show, onHide, itemId, itemName}) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  
  useEffect(() => {
    if (show && itemId) {
      fetchItemHistory(itemId);
    }
  }, [show, itemId]);
  
  const fetchItemHistory = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      const response = await axios.get(`https://rumahakrilik.id/api/inventory/${id}/history/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching item history:', error);
      toast.error('Gagal memuat histori item');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Histori {itemName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Memuat histori...</p>
          </div>
        ) : (
          <>
            {history.length === 0 ? (
              <p className="text-center">Tidak ada data histori untuk aset ini</p>
            ) : (
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Tipe</th>
                    <th>Jumlah</th>
                    <th>Pemohon</th>
                    <th>Diproses Oleh</th>
                    <th>Referensi</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item, index) => (
                    <tr key={index}>
                      <td>{new Date(item.timestamp).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                      <td>
                        <Badge bg={item.type === 'in' ? 'success' : 'danger'}>
                          {item.type === 'in' ? 'Masuk' : 'Keluar'}
                        </Badge>
                      </td>
                      <td>{item.quantity}</td>
                      <td>{item.requested_by || '-'}</td>
                      <td>{item.handled_by || '-'}</td>
                      <td>{item.reference || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Tutup</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DaftarAset;